/**
 * Axios adapter over the platform's payload-crypto library, for the NextGen custom-domain
 * deployment (PK-5181).
 *
 * This app is a microfrontend: the NextGen shell appends its bundle as a <script> into its OWN
 * document, so there is no build of ours to put a <script src=".../payload-crypto.min.js"> tag
 * into. The shell already loads that library -- gated on its custom-domain build -- and it lands
 * on the one window we share with it. So the library is never bundled and never loaded here; it
 * is borrowed.
 *
 * Two consequences the plumbing below depends on:
 *
 *  - configure() is NOT called. window.PayloadCrypto is a single instance shared with the shell,
 *    which has already configured it with its own error reporting. Configuring it again would
 *    replace that, and the handshake, session key and IndexedDB record are shared either way.
 *
 *  - Every call is guarded rather than the interceptors being registered once. Our bundle is
 *    appended at an arbitrary point in the shell's lifecycle, so the library may not be resolved
 *    yet; a missing library degrades to plaintext instead of throwing.
 *
 * No crypto happens here. Everything below is plumbing between that library and axios.
 */
import { gatewayBaseUrlOverride, isCustomDomain } from "../utils/config";

const REINIT_HEADER = "x-lsq-enc-reinit";

const lib = () => (typeof window === "undefined" ? undefined : window.PayloadCrypto);

export const isPayloadCryptoAvailable = () => Boolean(lib());

const parseIfJson = (value) => {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    // A sealed response that is not JSON is legitimate; hand back the plaintext untouched.
    return value;
  }
};

/**
 * axios splits a request across url/params/data, but prepareRequest wants one url string and one
 * body. getUri with an empty baseURL applies the instance's own param serializer, so arrays and
 * null params serialize exactly as they would on an unencrypted request.
 */
const toRequestUrl = (instance, config) => {
  try {
    return instance.getUri({ ...config, baseURL: "" });
  } catch {
    return config.url;
  }
};

/**
 * 409 is the gateway rejecting a ticket it can no longer unseal -- an expired or rotated session.
 * Anything else, or a request that has already been replayed once, is handed straight back so a
 * persistent 409 cannot loop.
 */
const shouldReplayAfterReinit = (payloadCrypto, error) => {
  if (!payloadCrypto) {
    return false;
  }
  if (error?.response?.status !== 409) {
    return false;
  }
  return Boolean(error.config) && !error.config.lsqCryptoRetried;
};

/**
 * Unseal a sealed error body in place.
 *
 * The success interceptor unseals; the error path did not, and it is the only other place a
 * sealed body reaches a call site. Call sites read error.response.data.message, which on a
 * sealed body is a property of a JWE string rather than an object, so it comes back undefined
 * and the UI falls back to a generic message, losing what the server actually said.
 *
 * decryptResponse returns its input unchanged when the body is not sealed, so this is inert on
 * plaintext errors. A failure here must never replace the original error: the caller needs the
 * status it actually got, not a decryption fault.
 */
const unsealErrorBody = async (payloadCrypto, error) => {
  const response = error?.response;
  if (!payloadCrypto || !response || response.data === undefined || response.data === null) {
    return;
  }

  try {
    const decrypted = await payloadCrypto.decryptResponse(response.data);
    if (decrypted !== response.data) {
      response.data = parseIfJson(decrypted);
    }
  } catch {
    /* leave the body exactly as it arrived */
  }
};

export function attachPayloadCrypto(instance) {
  // Off the custom domain there is no gateway at `${window.location.origin}/api/gw` to hand a
  // sealed request to, and the API calls are cross-origin anyway. Register nothing at all there
  // rather than pay an interceptor hop per request for a branch that never fires.
  if (!isCustomDomain()) {
    return instance;
  }

  instance.interceptors.request.use(async (config) => {
    const payloadCrypto = lib();
    if (!payloadCrypto) {
      return config;
    }

    const gatewayBaseUrl = gatewayBaseUrlOverride();

    // Stashed before anything is sealed so a 409 retry re-seals from the original rather than
    // sealing the ciphertext a second time.
    if (!config.lsqCryptoOriginal) {
      config.lsqCryptoOriginal = { url: config.url, params: config.params, data: config.data };
    }

    // prepareRequest resolves to { url, body, encrypted, headers } -- an object, not a tuple.
    const prepared = await payloadCrypto.prepareRequest(toRequestUrl(instance, config), {
      body: config.data,
      gatewayBaseUrl,
    });

    config.url = prepared.url;
    // The params are now inside `url`; leaving them set would append them a second time.
    config.params = undefined;
    if (prepared.body !== null && prepared.body !== undefined) {
      config.data = prepared.body;
    }
    // Its headers carry the ticket and, once a body has been sealed, Content-Type:
    // application/jose. The instance default of application/json would make the gateway
    // read a JWE as JSON, so these have to win over the instance headers.
    config.headers = { ...config.headers, ...prepared.headers };

    return config;
  });

  instance.interceptors.response.use(
    async (response) => {
      const payloadCrypto = lib();
      if (!payloadCrypto) {
        return response;
      }

      const decrypted = await payloadCrypto.decryptResponse(response.data);
      if (decrypted !== response.data) {
        response.data = parseIfJson(decrypted);
      }

      // The gateway asks for a fresh session out of band; renewing now keeps the cost off the
      // next request's critical path.
      if (response.headers?.[REINIT_HEADER]) {
        await payloadCrypto.reinit({ gatewayBaseUrl: gatewayBaseUrlOverride() });
      }

      return response;
    },
    async (error) => {
      const payloadCrypto = lib();

      // Before any early return, and for every status: a sealed body is lost either way.
      await unsealErrorBody(payloadCrypto, error);
      if (!shouldReplayAfterReinit(payloadCrypto, error)) {
        return Promise.reject(error);
      }

      try {
        await payloadCrypto.reinit({ gatewayBaseUrl: gatewayBaseUrlOverride() });
      } catch {
        return Promise.reject(error);
      }

      const { config } = error;
      return instance.request({
        ...config,
        ...(config.lsqCryptoOriginal || {}),
        lsqCryptoOriginal: undefined,
        lsqCryptoRetried: true,
      });
    }
  );

  return instance;
}
